import { useState, useEffect, useCallback } from "react";
import { orderAPI } from "../../services/api";
import socketService from "../../services/socket";
import toast from "react-hot-toast";
import {
  Package,
  User,
  Phone,
  IndianRupee,
  Download,
  Search,
  ChevronLeft,
  ChevronRight,
  Eye,
  Trash2,
  MapPin,
  Copy,
  ExternalLink,
  CheckCircle,
  Clock,
} from "lucide-react";

const OrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [stats, setStats] = useState({});

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        search: searchTerm,
      };

      const response = await orderAPI.getAll(params);
      setOrders(response.data.data.orders);
      setPagination(response.data.data.pagination);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    fetchOrders();
    fetchStats();

    // Connect to Socket.IO for real-time updates
    socketService.connect();

    // Listen for new orders
    const handleNewOrder = (data) => {
      toast.success(data.message || "New order received!");
      // Refresh orders and stats
      fetchOrders();
      fetchStats();
    };

    socketService.onNewOrder(handleNewOrder);

    // Cleanup on unmount
    return () => {
      socketService.offNewOrder(handleNewOrder);
    };
  }, [fetchOrders]);

  const fetchStats = async () => {
    try {
      const response = await orderAPI.getStats();
      setStats(response.data.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleExportOrders = async (format = "csv") => {
    try {
      const loadingToast = toast.loading("Preparing download...");

      if (format === "csv") {
        // For CSV, we need to handle the response differently
        const response = await fetch(
          `${
            import.meta.env.VITE_API_URL || "http://localhost:5000"
          }/api/orders/export/data?format=csv&limit=50`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
            },
          }
        );

        if (!response.ok) throw new Error("Export failed");

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `orders_${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        // For JSON format
        const response = await orderAPI.exportData({
          limit: 50,
          format: "json",
        });
        const dataStr = JSON.stringify(response.data.data, null, 2);
        const dataBlob = new Blob([dataStr], { type: "application/json" });

        const url = window.URL.createObjectURL(dataBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `orders_${Date.now()}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }

      toast.dismiss(loadingToast);
      toast.success(`Orders exported successfully as ${format.toUpperCase()}`);
    } catch (error) {
      console.error("Error exporting orders:", error);
      toast.error("Failed to export orders");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const openOrderModal = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  const handleDeleteOrder = async (orderId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this order? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      const loadingToast = toast.loading("Deleting order...");
      await orderAPI.delete(orderId);
      toast.dismiss(loadingToast);
      toast.success("Order deleted successfully");

      // Refresh orders and stats
      fetchOrders();
      fetchStats();
    } catch (error) {
      console.error("Error deleting order:", error);
      toast.error("Failed to delete order");
    }
  };

  const handleCopyAddress = (address) => {
    navigator.clipboard
      .writeText(address)
      .then(() => {
        toast.success("Address copied to clipboard!");
      })
      .catch(() => {
        toast.error("Failed to copy address");
      });
  };

  const handleOpenInMaps = (address) => {
    const encodedAddress = encodeURIComponent(address);
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    window.open(mapsUrl, "_blank");
  };

  const handleMarkAsDelivered = async (orderId, currentStatus) => {
    const newStatus = currentStatus === "delivered" ? "pending" : "delivered";
    const actionText = newStatus === "delivered" ? "delivered" : "pending";

    if (
      !window.confirm(
        `Are you sure you want to mark this order as ${actionText}?`
      )
    ) {
      return;
    }

    try {
      const loadingToast = toast.loading(`Marking order as ${actionText}...`);
      await orderAPI.updateDeliveryStatus(orderId, newStatus);
      toast.dismiss(loadingToast);
      toast.success(`Order marked as ${actionText} successfully`);

      // Refresh orders and stats
      fetchOrders();
      fetchStats();

      // Update selected order if modal is open
      if (selectedOrder && selectedOrder._id === orderId) {
        setSelectedOrder((prev) => ({
          ...prev,
          deliveryStatus: newStatus,
          deliveredAt: newStatus === "delivered" ? new Date() : null,
        }));
      }
    } catch (error) {
      console.error("Error updating delivery status:", error);
      toast.error(`Failed to mark order as ${actionText}`);
    }
  };

  if (loading && orders.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#305669]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Order Management
          </h1>
          <p className="text-gray-600">Manage and track all customer orders</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center">
              <Package className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Total Orders
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.totalOrders || 0}
                </p>
              </div>
            </div>
          </div>

          {/* <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center">
              <Calendar className="h-8 w-8 text-yellow-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Pending Orders
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.pendingOrders || 0}
                </p>
              </div>
            </div>
          </div> */}

          {/* <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center">
              <Package className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Delivered</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.deliveredOrders || 0}
                </p>
              </div>
            </div>
          </div> */}

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center">
              <IndianRupee className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Total Revenue
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {/* {(stats.totalRevenue || 0)} */}
                  {formatCurrency(stats.totalRevenue || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#305669] focus:border-transparent"
                />
              </div>
            </div>

            {/* Export Buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => handleExportOrders("csv")}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                <Download className="h-4 w-4" />
                <span>Export CSV</span>
              </button>
              <button
                onClick={() => handleExportOrders("json")}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Download className="h-4 w-4" />
                <span>Export JSON</span>
              </button>
            </div>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table
              className="min-w-full divide-y divide-gray-200"
              style={{ minWidth: "1200px" }}
            >
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Delivery Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {orders.map((order) => (
                  <tr key={order._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">
                          #{order._id.slice(-8)}
                        </div>
                        <div className="text-gray-500">
                          {formatDate(order.orderDate)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="h-4 w-4 text-gray-400 mr-2" />
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">
                            {order.customerName}
                          </div>
                          <div className="text-gray-500 flex items-center">
                            <Phone className="h-3 w-3 mr-1" />
                            {order.customerPhone}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">
                          {order.productName}
                        </div>
                        <div className="text-gray-500">
                          {order.quantity} × {order.bagSize}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm max-w-48">
                        <div
                          className="text-gray-900 truncate mb-1"
                          title={order.address}
                        >
                          {order.address}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleCopyAddress(order.address)}
                            className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                            title="Copy address"
                          >
                            <Copy className="h-3 w-3 cursor-pointer" />
                            Copy
                          </button>
                          <button
                            onClick={() => handleOpenInMaps(order.address)}
                            className="flex items-center gap-1 px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                            title="Open in Google Maps"
                          >
                            <ExternalLink className="h-3 w-3" />
                            Maps
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {order.deliveryStatus === "delivered" ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Delivered
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <Clock className="h-3 w-3 mr-1" />
                            Pending
                          </span>
                        )}
                      </div>
                      {order.deliveredAt && (
                        <div className="text-xs text-gray-500 mt-1">
                          {formatDate(order.deliveredAt)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(order.total)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {order.paymentMethod}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => openOrderModal(order)}
                          className="text-[#305669] hover:text-[#1785A] cursor-pointer"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4 cursor-pointer" />
                        </button>
                        <button
                          onClick={() =>
                            handleMarkAsDelivered(
                              order._id,
                              order.deliveryStatus
                            )
                          }
                          className={`${
                            order.deliveryStatus === "delivered"
                              ? "text-yellow-600 hover:text-yellow-800"
                              : "text-green-600 hover:text-green-800"
                          }`}
                          title={
                            order.deliveryStatus === "delivered"
                              ? "Mark as Pending"
                              : "Mark as Delivered"
                          }
                        >
                          {order.deliveryStatus === "delivered" ? (
                            <Clock className="h-4 w-4 cursor-pointer" />
                          ) : (
                            <CheckCircle className="h-4 w-4 cursor-pointer" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteOrder(order._id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete Order"
                        >
                          <Trash2 className="h-4 w-4 cursor-pointer" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={!pagination.hasPrev}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() =>
                    setCurrentPage((prev) =>
                      Math.min(prev + 1, pagination.totalPages)
                    )
                  }
                  disabled={!pagination.hasNext}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing page{" "}
                    <span className="font-medium">
                      {pagination.currentPage}
                    </span>{" "}
                    of{" "}
                    <span className="font-medium">{pagination.totalPages}</span>{" "}
                    ({pagination.totalOrders} total orders)
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={!pagination.hasPrev}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() =>
                        setCurrentPage((prev) =>
                          Math.min(prev + 1, pagination.totalPages)
                        )
                      }
                      disabled={!pagination.hasNext}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Order Detail Modal */}
        {showOrderModal && selectedOrder && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Order Details - #{selectedOrder._id.slice(-8)}
                  </h3>
                  <button
                    onClick={() => setShowOrderModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        Customer Information
                      </h4>
                      <p>
                        <strong>Name:</strong> {selectedOrder.customerName}
                      </p>
                      <p>
                        <strong>Phone:</strong> {selectedOrder.customerPhone}
                      </p>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        Order Information
                      </h4>
                      <p>
                        <strong>Date:</strong>{" "}
                        {formatDate(selectedOrder.orderDate)}
                      </p>
                      <p className="flex items-center mt-2">
                        <strong>Status:</strong>
                        <span className="ml-2">
                          {selectedOrder.deliveryStatus === "delivered" ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Delivered
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              <Clock className="h-3 w-3 mr-1" />
                              Pending
                            </span>
                          )}
                        </span>
                      </p>
                      {selectedOrder.deliveredAt && (
                        <p>
                          <strong>Delivered At:</strong>{" "}
                          {formatDate(selectedOrder.deliveredAt)}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">
                      Product Details
                    </h4>
                    <p>
                      <strong>Product:</strong> {selectedOrder.productName}
                    </p>
                    <p>
                      <strong>Bag Size:</strong> {selectedOrder.bagSize}
                    </p>
                    <p>
                      <strong>Quantity:</strong> {selectedOrder.quantity} bags
                    </p>
                    <p>
                      <strong>Price per bag:</strong>{" "}
                      {formatCurrency(selectedOrder.pricePerBag)}
                    </p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">
                      Delivery Address
                    </h4>
                    <div className="bg-gray-50 p-3 rounded-lg border">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-gray-800 mb-2">
                            {selectedOrder.address}
                          </p>
                          {selectedOrder.landmark && (
                            <p className="text-sm text-gray-600">
                              <strong>Landmark:</strong>{" "}
                              {selectedOrder.landmark}
                            </p>
                          )}
                          {selectedOrder.distance && (
                            <p className="text-sm text-gray-600">
                              <strong>Distance:</strong>{" "}
                              {selectedOrder.distance.toFixed(1)} km
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col gap-2 ml-3">
                          <button
                            onClick={() =>
                              handleCopyAddress(selectedOrder.address)
                            }
                            className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                            title="Copy address"
                          >
                            <Copy className="h-3 w-3" />
                            Copy
                          </button>
                          <button
                            onClick={() =>
                              handleOpenInMaps(selectedOrder.address)
                            }
                            className="flex items-center gap-1 px-3 py-1.5 text-xs bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
                            title="Open in Google Maps"
                          >
                            <MapPin className="h-3 w-3" />
                            Maps
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">
                      Payment Details
                    </h4>
                    <p>
                      <strong>Payment Method:</strong>{" "}
                      {selectedOrder.paymentMethod}
                    </p>
                    <p>
                      <strong>Subtotal:</strong>{" "}
                      {formatCurrency(selectedOrder.subtotal)}
                    </p>
                    <p>
                      <strong>Delivery Charge:</strong>{" "}
                      {formatCurrency(selectedOrder.deliveryCharge)}
                    </p>
                    <p>
                      <strong>Total Amount:</strong>{" "}
                      {formatCurrency(selectedOrder.total)}
                    </p>
                  </div>
                </div>

                <div className="mt-6 flex justify-between">
                  <button
                    onClick={() =>
                      handleMarkAsDelivered(
                        selectedOrder._id,
                        selectedOrder.deliveryStatus
                      )
                    }
                    className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                      selectedOrder.deliveryStatus === "delivered"
                        ? "bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                        : "bg-green-100 text-green-700 hover:bg-green-200"
                    }`}
                  >
                    {selectedOrder.deliveryStatus === "delivered" ? (
                      <>
                        <Clock className="h-4 w-4" />
                        Mark as Pending
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4" />
                        Mark as Delivered
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setShowOrderModal(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderManagement;
